import { htmlToText } from "@/lib/richtext";

export const PRESETS = [
  ["general", "text", "General Information"], ["personality", "text", "Personality"], ["appearance", "text", "Physical Appearance"],
  ["early", "text", "Early History"], ["journey", "journey", "Journey"], ["timeline", "timeline", "Character Timeline"],
  ["powers", "text", "Powers and Abilities"], ["weaknesses", "text", "Weaknesses"], ["skills", "text", "Skills and Hobbies"],
  ["relationships", "relationships", "Relationships"], ["trivia", "text", "Trivia"], ["name", "text", "Name"],
  ["tropes", "tropes", "Tropes"], ["quotes", "quotes", "Quotes"],
];
export const TOP_IDS = ["general", "personality", "appearance"];
export const KIND_LABEL = { text: "text", journey: "tabs", timeline: "timeline", relationships: "list", tropes: "list", quotes: "list" };
export const REL_GROUPS = ["Romances", "Family", "Friends", "Allies", "Enemies"];

export const makeBlock = (id, kind, title, preset = false) => ({
  id, kind, title, preset, html: "", tabs: kind === "journey" ? [{ name: "Part 1", html: "" }] : [], points: [], notes: [], tropes: [], quotes: [],
});
export const presetBlocks = () => PRESETS.map(([id, kind, title]) => makeBlock(id, kind, title, true));
export const newId = () => `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const relFacts = (page) => page.sections?.find((s) => s.title === "Relationships")?.facts || [];
export const relKey = (item) => item.page_id || `t:${(item.text || "").trim().toLowerCase()}`;
export const relEntries = (page) => REL_GROUPS.map((g) => ({ group: g, items: (relFacts(page).find((f) => f.key === g)?.items || []).filter((i) => i.page_id || i.text?.trim()) })).filter((g) => g.items.length);

const quoteText = (q) => (q?.lines || []).map((l) => `${l.speaker?.text ? `${l.speaker.text}: ` : ""}${l.text}`).join("\n");
export const quoteEmpty = (q) => !q || !(q.lines || []).some((l) => l.text.trim());
const plain = (html) => htmlToText(html || "").trim();

export const blockEmpty = (b, page) => {
  switch (b.kind) {
    case "journey": return !b.tabs.some((t) => plain(t.html));
    case "timeline": return !b.points.length;
    case "relationships": return !relEntries(page).length;
    case "tropes": return !b.tropes.length;
    case "quotes": return !b.quotes.some((q) => !quoteEmpty(q));
    default: return !plain(b.html);
  }
};

// Raw HTML/text of a block, for links, search and history
export const blockRaw = (b) => [b.html, ...b.tabs.map((t) => `${t.name}\n${t.html}`), ...b.points.map((p) => `${p.date} ${p.title}\n${p.description}`),
  ...b.notes.map((n) => n.html), b.tropes.join(", "), ...b.quotes.map(quoteText)].filter(Boolean).join("\n");
export const pageRaw = (page) => [page.content, quoteText(page.pinned_quote), ...(page.blocks || []).map(blockRaw)].filter(Boolean).join("\n");

const docBody = (b) => [plain(b.html), ...b.tabs.filter((t) => plain(t.html)).map((t) => `[${t.name}] ${plain(t.html)}`),
  ...b.points.map((p) => `• ${p.date} ${p.title} ${p.description}`), ...b.notes.map((n) => plain(n.html)), b.tropes.join(", "), ...b.quotes.map(quoteText)]
  .filter((x) => x && x.trim()).join("\n");
// Plain text with one heading per non-empty section, for comparing versions
export const pageDoc = (page) => [plain(page.content), page.pinned_quote && !quoteEmpty(page.pinned_quote) ? `## Pinned quote\n${quoteText(page.pinned_quote)}` : "",
  ...(page.blocks || []).map((b) => (docBody(b) ? `## ${b.title}\n${docBody(b)}` : ""))].filter(Boolean).join("\n\n");
export const pageHtml = (page) => JSON.stringify([page.content, page.pinned_quote, page.blocks]);

const esc = (s) => (s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const quoteHtml = (q) => `<blockquote>${q.lines.filter((l) => l.text.trim()).map((l) => `<p>${l.speaker?.text ? `<strong>${esc(l.speaker.text)}:</strong> ` : ""}${esc(l.text)}</p>`).join("")}</blockquote>`;
const asHtml = (b) => ({
  journey: () => b.tabs.filter((t) => plain(t.html)).map((t) => `<h3>${esc(t.name)}</h3>${t.html}`).join(""),
  timeline: () => `<ul>${b.points.map((p) => `<li><strong>${esc(p.date)}</strong> ${esc(p.title)}${p.description ? ` — ${esc(p.description)}` : ""}</li>`).join("")}</ul>`,
  tropes: () => `<p>${b.tropes.map(esc).join(", ")}</p>`,
  quotes: () => b.quotes.filter((q) => !quoteEmpty(q)).map(quoteHtml).join(""),
  relationships: () => b.notes.filter((n) => plain(n.html)).map((n) => n.html).join(""),
}[b.kind] || (() => b.html))();
const filled = (b) => (b.kind === "relationships" ? b.notes.some((n) => plain(n.html)) : !blockEmpty(b, {}));

// Character-only sections (and the pinned quote) that hold writing and would be lost outside Characters
export const charOnlyFilled = (blocks, pinned) => [...(quoteEmpty(pinned) ? [] : ["Pinned quote"]), ...blocks.filter((b) => b.kind !== "text" && filled(b)).map((b) => b.title)];

// Turns every section with writing into a plain custom text section, remembering where each came from
export const textPairs = (blocks, pinned) => [
  ...(quoteEmpty(pinned) ? [] : [{ src: null, blk: { ...makeBlock(newId(), "text", "Pinned quote"), html: quoteHtml(pinned) } }]),
  ...blocks.filter(filled).map((b) => ({ src: b, blk: { ...makeBlock(b.preset ? newId() : b.id, "text", b.title), html: asHtml(b) } })),
];

// Back to Characters: original sections return, and anything written while away is kept
export const restoreBlocks = (pairs, original, cur) => {
  let blocks = original;
  const extra = [];
  cur.forEach((c) => {
    const p = pairs.find((x) => x.blk.id === c.id);
    if (!p) return extra.push(c);
    if (c.html === p.blk.html && c.title === p.blk.title) return;
    if (p.src?.kind === "text") blocks = blocks.map((b) => (b.id === p.src.id ? { ...b, html: c.html, title: b.preset ? b.title : c.title } : b));
    else extra.push(c);
  });
  return [...blocks, ...extra];
};

export const uniqTropes = (list) => list.filter((t, i) => list.findIndex((x) => x.toLowerCase() === t.toLowerCase()) === i);
